import React, { useState, useEffect } from 'react';

const HeroSection = ({ onOpenPaymentPage, pricing = { price1Friend: 2975, price2Friends: 2975, originalPrice: 7000 } }) => {
  const price1 = pricing.price1Friend;
  const price2 = pricing.price2Friends || 2975;
  const originalPrice1 = 3999;
  const originalPrice2 = price2 * 3.33;
  const discount1 = 50;
  const discount2 = Math.round((1 - price2 / originalPrice2) * 100);
  const savings1 = 2000;
  const savings2 = originalPrice2 - price2;
  const formatPrice = (num) => '₹' + Number(num).toLocaleString('en-IN');

  // Countdown Timer State
  const [timeLeft, setTimeLeft] = useState({
    hours: 0,
    minutes: 5,
    seconds: 0
  });

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        let { hours, minutes, seconds } = prev;
        
        if (seconds > 0) {
          seconds--;
        } else if (minutes > 0) {
          minutes--;
          seconds = 59;
        } else if (hours > 0) {
          hours--;
          minutes = 59;
          seconds = 59;
        } else {
          // Reset timer when it reaches 0
          hours = 0;
          minutes = 5;
          seconds = 0;
        }
        
        return { hours, minutes, seconds };
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatTime = (num) => num.toString().padStart(2, '0');

  const features = [
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
      ),
      title: "10 Hours",
      subtitle: "Live Recorded"
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
        </svg>
      ),
      title: "Step By Step",
      subtitle: "Payment Recovery Training Guide"
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      title: "Watch it ANYTIME",
      subtitle: "At your convenience"
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
        </svg>
      ),
      title: "Learning Friendly",
      subtitle: ""
    }
  ];

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-24 pb-16">
      {/* Animated Background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-black"></div>
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-to-r from-amber-500/5 to-orange-500/5 rounded-full blur-3xl"></div>
      </div>

      {/* Grid Pattern Overlay */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `linear-gradient(rgba(251, 191, 36, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(251, 191, 36, 0.1) 1px, transparent 1px)`,
          backgroundSize: '50px 50px'
        }}></div>
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Headline */}
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-8xl font-black mb-6 leading-tight">
            <span className="block text-white">Say Goodbye to</span>
            <span className="inline-block bg-gradient-to-r from-amber-400 via-orange-500 to-red-500 bg-clip-text text-transparent animate-gradient">
              "Late Payments"
            </span>
          </h1>
          
          <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-amber-400 mb-6 max-w-4xl mx-auto leading-relaxed px-2">
            Master the Advance Payment Collection System with <span className="text-white text-3xl sm:text-4xl md:text-5xl lg:text-6xl">28</span> Proven Collection Techniques for Indian Businesses
          </h2>
          
          <p className="text-base sm:text-lg text-amber-500 font-semibold mb-4">
            (Tech. #7 and #9 are Game Changers)
          </p>
          
          <p className="text-base sm:text-lg md:text-xl text-gray-400 max-w-3xl mx-auto italic px-2">
            "Transform Your Payment Follow-Up Process: Secure Commitments and Handle Calls Effectively with my <span className="text-green-400 font-bold text-xl sm:text-2xl md:text-3xl animate-blink-green">7-Day Money Back Guarantee</span>"
          </p>
          
          <p className="mt-4 text-lg sm:text-xl text-white font-bold">
            <span className="italic">Rushneek Singh's Promise</span>
          </p>
        </div>

        {/* Vimeo Video Section */}
        <div className="relative mb-12 max-w-4xl mx-auto">
          <div className="relative group">
            {/* Glowing Border Effect */}
            <div className="absolute -inset-1 bg-gradient-to-r from-amber-500 via-orange-500 to-amber-500 rounded-2xl blur-lg opacity-40 group-hover:opacity-70 transition-all duration-500"></div>
            
            {/* Video Container */}
            <div className="relative bg-gray-900 rounded-2xl overflow-hidden border border-amber-500/30 shadow-2xl shadow-amber-500/10">
              <div className="aspect-video">
                <iframe
                  src="https://player.vimeo.com/video/1200069171?badge=0&autopause=0&player_id=0&app_id=58479"
                  className="w-full h-full"
                  frameBorder="0"
                  allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
                  allowFullScreen
                  title="Calling Scripts Training Preview"
                ></iframe>
              </div>
            </div>
            
            {/* Decorative Elements */}
            <div className="absolute -top-4 -right-4 w-8 h-8 bg-amber-500 rounded-full blur-md opacity-50"></div>
            <div className="absolute -bottom-4 -left-4 w-6 h-6 bg-orange-500 rounded-full blur-md opacity-50"></div>
          </div>
          
          {/* Video Caption */}
          <p className="text-center text-gray-400 mt-4 text-sm">
            <span className="text-amber-400">▶</span> Watch the preview to see what you'll learn
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 mb-12">
          {features.map((feature, index) => (
            <div 
              key={index}
              className="group relative bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm rounded-2xl p-6 border border-gray-700/50 hover:border-amber-500/50 transition-all duration-500 hover:scale-105 hover:shadow-xl hover:shadow-amber-500/10"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-amber-500/0 to-orange-500/0 group-hover:from-amber-500/10 group-hover:to-orange-500/10 rounded-2xl transition-all duration-500"></div>
              <div className="relative z-10 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-amber-500/20 to-orange-500/20 rounded-2xl text-amber-400 mb-4 group-hover:scale-110 transition-transform duration-300">
                  {feature.icon}
                </div>
                <h3 className="text-lg font-bold text-white mb-1">{feature.title}</h3>
                <p className="text-sm text-gray-400">{feature.subtitle}</p>
              </div>
            </div>
          ))}
        </div>

        {/* CTA Card - Single */}
        <div className="max-w-2xl mx-auto px-4">
          <div className="relative group">
            <div className="absolute -inset-1 bg-gradient-to-r from-amber-500/40 via-orange-500/40 to-amber-500/40 rounded-3xl blur-xl opacity-60"></div>
            <div className="relative bg-gradient-to-br from-slate-800 via-slate-900 to-slate-800 rounded-3xl border border-amber-500/30 p-6 text-center overflow-hidden shadow-2xl">
              <div>
                <div className="h-6 mb-2"></div>
                
                {/* Timer */}
                <div className="mb-4">
                  <p className="text-gray-400 text-xs mb-3 uppercase tracking-widest font-medium">Offer Expires In</p>
                  <div className="flex items-center justify-center gap-2">
                    <div className="relative">
                      <div className="absolute inset-0 bg-red-500 rounded-xl blur-sm opacity-40"></div>
                      <div className="relative bg-gradient-to-br from-red-500 to-red-700 text-white w-14 h-14 rounded-xl flex items-center justify-center text-2xl font-black shadow-lg border border-red-400/30">
                        {formatTime(timeLeft.minutes)}
                      </div>
                      <p className="text-gray-500 text-[9px] mt-1 uppercase tracking-wider">Minutes</p>
                    </div>
                    <span className="text-2xl font-black text-amber-500 animate-pulse mb-5">:</span>
                    <div className="relative">
                      <div className="absolute inset-0 bg-red-500 rounded-xl blur-sm opacity-40"></div>
                      <div className="relative bg-gradient-to-br from-red-500 to-red-700 text-white w-14 h-14 rounded-xl flex items-center justify-center text-2xl font-black shadow-lg border border-red-400/30">
                        {formatTime(timeLeft.seconds)}
                      </div>
                      <p className="text-gray-500 text-[9px] mt-1 uppercase tracking-wider">Seconds</p>
                    </div>
                  </div>
                </div>

                <p className="text-red-400 text-xl md:text-2xl font-bold mb-4">
                  Price rises to {formatPrice(originalPrice1)} after timer ends!
                </p>

                <div className="mb-4">
                  <p className="text-red-500 line-through text-xl font-black">{formatPrice(originalPrice1)}</p>
                  <div className="flex items-center justify-center gap-2">
                    <span className="text-4xl md:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-400">
                      {formatPrice(price1)}
                    </span>
                    <span className="bg-green-500/20 text-green-400 text-xs font-bold px-2 py-1 rounded-full border border-green-500/30">
                      {discount1}% OFF
                    </span>
                  </div>
                  <p className="text-green-400 font-semibold mt-2 flex items-center justify-center gap-1 text-sm">
                    <span>🎉</span> Save {formatPrice(savings1)} Today!
                  </p>
                </div>
              </div>

              <button 
                onClick={() => onOpenPaymentPage(price1)}
                className="group/btn relative inline-flex items-center justify-center w-full py-4 px-6 text-base font-bold text-black bg-gradient-to-r from-amber-400 via-yellow-400 to-orange-500 rounded-xl shadow-lg shadow-amber-500/30 overflow-hidden transition-all duration-300 hover:scale-[1.02] hover:shadow-amber-500/50"
              >
                <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/30 to-white/0 -translate-x-full group-hover/btn:translate-x-full transition-transform duration-700"></span>
                <span className="relative flex items-center gap-2">
                  Yes, I Want This
                  <svg className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <style dangerouslySetInnerHTML={{__html: `
        @keyframes gradient {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        .animate-gradient {
          background-size: 200% auto;
          animation: gradient 3s ease infinite;
        }
        @keyframes blink-slow {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.65; }
        }
        .animate-blink-slow {
          animation: blink-slow 1.5s ease-in-out infinite;
        }
        @keyframes blink-green {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.6; }
        }
        .animate-blink-green {
          animation: blink-green 1.2s ease-in-out infinite;
        }
      `}} />
    </section>
  );
};

export default HeroSection;
